import { supabase } from './supabase';
import {
  toAnnouncement,
  isAnnouncementLive,
  type NetworkAnnouncement,
  type AnnouncementSeverity,
} from './announcements';

/**
 * Annonce réseau (brique A) — I/O Supabase.
 * Lecture : la table est en lecture publique (RLS), donc le bandeau s'affiche même
 * pour un visiteur anonyme. Écriture : RPC sécurisée `set_network_announcement`
 * (super_admin uniquement), cf. sql/21_announcements.sql. Logique pure dans announcements.ts.
 */

/** Identifiant fixe de l'annonce réseau unique (singleton, cf. sql/21). */
const SINGLETON_ID = '00000000-0000-0000-0000-000000000001';

/**
 * Charge l'annonce réseau courante. Renvoie l'objet uniquement s'il doit s'afficher
 * MAINTENANT (actif + dans la fenêtre). Sinon null (pas de bandeau).
 * Tolérant : toute erreur réseau → null (on n'affiche rien plutôt que de planter un site).
 */
export async function loadActiveAnnouncement(now: Date = new Date()): Promise<NetworkAnnouncement | null> {
  const { data, error } = await supabase
    .from('network_announcements')
    .select('message, severity, is_active, starts_at, ends_at')
    .eq('id', SINGLETON_ID)
    .maybeSingle();

  if (error || !data) return null;
  const a = toAnnouncement(data);
  return isAnnouncementLive(a, now) ? a : null;
}

/** Charge l'annonce telle quelle (même inactive) pour l'éditeur SuperAdmin. */
export async function loadAnnouncementDraft(): Promise<NetworkAnnouncement | null> {
  const { data, error } = await supabase
    .from('network_announcements')
    .select('message, severity, is_active, starts_at, ends_at')
    .eq('id', SINGLETON_ID)
    .maybeSingle();
  if (error || !data) return null;
  return toAnnouncement(data);
}

export interface SaveAnnouncementInput {
  message: string;
  severity: AnnouncementSeverity;
  isActive: boolean;
  startsAt?: string | null;
  endsAt?: string | null;
}

export interface SaveAnnouncementResult {
  ok: boolean;
  error?: string;
}

/** Publie / met à jour l'annonce réseau via la RPC sécurisée (super_admin). */
export async function saveAnnouncement(input: SaveAnnouncementInput): Promise<SaveAnnouncementResult> {
  const { error } = await supabase.rpc('set_network_announcement', {
    p_message: input.message,
    p_severity: input.severity,
    p_is_active: input.isActive,
    p_starts_at: input.startsAt ?? null,
    p_ends_at: input.endsAt ?? null,
  });
  if (error) {
    return { ok: false, error: `Publication de l'annonce impossible : ${error.message}` };
  }
  return { ok: true };
}
